import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke, listen, platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, relativeDays, prettyDate, todayLocal, parseNum, SOURCE_LABELS, MEAL_SLOTS,
  type Meal, type MealSource, type MealSlot, type WeightEntry, type HealthSignals, type HeadlineFact, type MealEstimate,
} from "./store";

/** Display names for the parts of a day. Plain nouns — naming a part of the day is structure;
 *  anything prescriptive would breach the health fence. */
const SLOT_LABEL: Record<Exclude<MealSlot, "">, string> = {
  breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snacks",
};

/** Pre-select the slot from the clock so the common case is zero taps. It is only a DEFAULT —
 *  the picker is always visible and always changeable, because a meal logged late in the evening
 *  is very often lunch being caught up on. */
function guessSlot(): MealSlot {
  const h = new Date().getHours();
  if (h < 11) return "breakfast";
  if (h < 15) return "lunch";
  if (h < 21) return "dinner";
  return "snack";
}
import { weeklyRecap, askHealth, parseLabelWithAI, estimateMeal, medicalRefusal, openWebLogin, BrainError, type BrainErrorCode, estimateMealPhoto } from "./brain";
import { parseNutritionLabel, labelParseUsable } from "./label";
import { OrioraBadge } from "./OrioraBadge";
import glyphBreakfast from "./assets/glyph-breakfast.svg";
import glyphLunch from "./assets/glyph-lunch.svg";
import glyphDinner from "./assets/glyph-dinner.svg";
import glyphSnacks from "./assets/glyph-snacks.svg";
import glyphCapture from "./assets/glyph-capture.svg";
import emptyFirstCapture from "./assets/empty-first-capture.svg";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import Chat from "./chat/Chat";
import {
  Sparkles, Trash2, X, Lock, WifiOff, RefreshCw, Camera, AlertCircle, Plus,
  Copy, Check, Flame, Coins, Scale, Utensils, Barcode, ScanLine, KeyRound, Pencil, CalendarDays,
} from "lucide-react";

const LIST_CAP = 50000;
// SCREENSHOT=0 — sim-only auth bypass for identity screenshots. MUST stay 0 in committed/shipped
// code (gate-check asserts it). Same pattern as oriora_encore.
const SCREENSHOT = 0;
type FactTone = HeadlineFact["tone"];

/**
 * a plate seen from above — the app photographs meals and calls the list 'your plates'.
 * Replaces the lucide `Sparkles` that 20 of the fleet's 26 chat launchers rendered in common;
 * the button outline changes with it, because one glyph in two colours is still one control
 * (Guideline 4.3(a)).
 */
function PlateMark({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="4.4" stroke="currentColor" strokeWidth="1.4" opacity="0.6" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2 #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns ai-health-tracker-coach://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // ai-health-tracker-coach://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Oriora Meal Tracker"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <LoadingGate />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function LoadingGate() {
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "1.5rem" }}>
      <div style={{ textAlign: "center" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><Utensils size={34} /></div>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.4rem", fontSize: "var(--fs-sm)" }}>
          <Sparkles size={13} className="pulse-soft" style={{ color: "var(--accent)" }} /> Opening your log…
        </div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up empty-glow" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><Utensils size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center", marginBottom: "0.35rem" }}>Oriora Meal Tracker</div>
        <h1 className="serif" style={{ fontSize: "1.6rem", margin: "0 0 0.5rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "1.4rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your meals and weight
          live on your device unless you back them up; when you ask for an AI estimate, recap, or
          photo read, that passes through your Oriora account, which doesn't retain it. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("ai-health-tracker-coach");
              else if (p === "macos") await signInMac("ai-health-tracker-coach");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/ai-health-tracker-coach/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? (<><Sparkles size={14} className="pulse-soft" /> Signing in…</>) : "Sign in with Oriora"}
        </button>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.3rem", fontSize: "var(--fs-xxs)", marginTop: "1rem" }}>
          <Lock size={11} /> Your food and weight log lives on this device — you choose what's sent or backed up
        </div>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [weights, setWeights] = useState<WeightEntry[]>([]);
  const [editing, setEditing] = useState<Meal | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Meal | null>(null);
  const [showWeights, setShowWeights] = useState(false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities.
  const [chatOpen, setChatOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // CSV export uses the desktop save-file dialog, which doesn't exist on iOS — hide there.
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => { try { setIsDesktop(platform() !== "ios" && platform() !== "android"); } catch { /* keep default */ } }, []);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const aiRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  // The signals engine is a LOCAL Rust call — free, no billed call, no network. It was only
  // ever used to feed the paid recap, so the home screen showed none of it and led with a
  // generic "big number today" hero instead. That hero is the shared fleet skeleton; this is
  // the app's own moat, computed for nothing. Surface it. (Same finding as Skein's pattern_fit.)
  const [homeSignals, setHomeSignals] = useState<HealthSignals | null>(null);
  const reload = useCallback(async () => {
    setMeals(await store.listMeals({ limit: LIST_CAP }));
    setWeights(await store.listWeights({ limit: LIST_CAP }));
    try { setHomeSignals(await store.computeSignals(todayLocal())); } catch { setHomeSignals(null); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): Add / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add": setEditing(null); setShowForm(true); break;
        case "recap":
          aiRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const today = todayLocal();
  const todayMeals = useMemo(() => meals.filter((m) => m.eaten_on === today), [meals, today]);
  const todayCalories = useMemo(() => todayMeals.reduce((s, m) => s + m.calories, 0), [todayMeals]);
  const daysLogged = useMemo(() => new Set(meals.map((m) => m.eaten_on)).size, [meals]);

  // Group meals by day for the list (already ordered eaten_on DESC, created_at DESC).
  // Grouped by day, and within a day by SLOT. The slot breakdown is what lets the log show the
  // shape of a day (which parts are filled, which are open) instead of a flat run of cards —
  // the structure every food-logging app uses, because "what does my day look like" is the
  // question people actually open the app with.
  const byDay = useMemo(() => {
    const groups: { date: string; rows: Meal[]; calories: number }[] = [];
    for (const m of meals) {
      const g = groups[groups.length - 1];
      if (g && g.date === m.eaten_on) { g.rows.push(m); g.calories += m.calories; }
      else groups.push({ date: m.eaten_on, rows: [m], calories: m.calories });
    }
    return groups;
  }, [meals]);

  /** Split one day's meals into the four slots plus anything unsorted (pre-slot meals). */
  const splitSlots = useCallback((rows: Meal[]) => {
    const out: { slot: MealSlot; label: string; rows: Meal[]; calories: number }[] =
      MEAL_SLOTS.map((slot) => ({ slot, label: SLOT_LABEL[slot], rows: [], calories: 0 }));
    const other: Meal[] = [];
    for (const m of rows) {
      const bucket = out.find((o) => o.slot === m.slot);
      if (bucket) { bucket.rows.push(m); bucket.calories += m.calories; }
      else other.push(m);
    }
    if (other.length) {
      out.push({ slot: "", label: "Other", rows: other, calories: other.reduce((a, m) => a + m.calories, 0) });
    }
    return out;
  }, []);

  // Today lives on the board above, so the history section counts only the days before it.
  const earlierCount = useMemo(() => meals.filter((m) => m.eaten_on !== todayLocal()).length, [meals]);

  const doExport = useCallback(() => {
    if (meals.length === 0 && weights.length === 0) return;
    const { csv, filename } = buildExport(meals, weights);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [meals, weights]);
  exportRef.current = doExport;

  const [preset, setPreset] = useState<{ slot?: MealSlot; date?: string; assist?: "photo" | "label" | "barcode" }>({});
  function startForm(meal?: Meal, slot?: MealSlot, date?: string, assist?: "photo" | "label" | "barcode") {
    setEditing(meal ?? null);
    setPreset({ slot, date, assist });
    setShowForm(true);
  }
  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteMeal(id);
    await reload();
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      {/* The board's masthead: the date as an eyebrow, the day in the display face. */}
      <header style={{ marginBottom: "1.1rem" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <span className="eyebrow">{prettyDate(todayLocal())}</span>
          <div className="row" style={{ gap: "0.5rem", alignItems: "center", flexWrap: "nowrap" }}>
            <BackupMenu
              appName="Meal Tracker" fileSlug="meal-tracker" appScheme="ai-health-tracker-coach"
              hasData={meals.length > 0 || weights.length > 0} onRestored={reload}
              onExportCsv={isDesktop && (meals.length > 0 || weights.length > 0) ? doExport : undefined}
            />
            <AccountMenu email={email} appSlug="ai-health-tracker-coach" appName="Oriora Meal Tracker" onSignedOut={onSignedOut} />
            <AddFundsMenu />
          </div>
        </div>
        <h1 className="serif" style={{ fontSize: "var(--fs-2xl)", margin: "0.15rem 0 0" }}>{todayMeals.length === 0 ? "Good morning" : "Today"}</h1>
      </header>

      {offline && (
        <div role="status" className="notice notice-warn fade-up" style={{ marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>The AI recap is paused while you're offline — your meals and weight still save on this device.</span>
        </div>
      )}

      {/* R1 — CAPTURE LEADS. Photographing a meal is the first and largest thing on the home;
          label-scan, barcode and typing sit under it as visible peers. All four open the same
          Log-a-meal sheet, which carries every mode. */}
      <section className="capture fade-up" aria-label="Log a meal" style={{ marginBottom: "1.25rem" }}>
        <div className="capture-eye">
          <button type="button" className="capture-plate" onClick={() => startForm(undefined, undefined, todayLocal(), "photo")} aria-label="Photograph a meal">
            <img src={glyphCapture} alt="" aria-hidden="true" width={44} height={44} />
          </button>
        </div>
        <h2 className="capture-title">Photograph a meal</h2>
        {/* R1 (ready-state 2026-09-25) — the BETTER row was REACHABLE but never NAMED: "Barcode" was
            a bare word and nothing on the first screen said what this app does differently. Per
            BETTER-ROW-RESEARCH-2026-09-25.md §3, the strongest row on the account is
            no-subscription/no-trial, and its named home is this subtitle, "where the per-use cost cue
            already belongs". The leader's own FAQ is what makes it the strongest row (trial +
            "You can't cancel from inside the app") — but their PRICE is not on their site, so no
            figure of theirs is quoted here. Only our own terms are stated, and each is a fact:
            no subscription (wallet, per estimate) · barcode = Open Food Facts, client-side and free
            (`App.tsx:1360`) · label = on-device Vision OCR, native-only, hence the same platform gate. */}
        {/* ⚠️ NO AMOUNT, EVER. This line first shipped as "you pay a few cents" — a price statement,
            and not ours to make: the router picks the model, so the per-estimate cost is not a fixed
            number this app can promise. The fleet's cue wording never names an amount (KnowCode
            "charged per ask on your own key — no subscription"; Stash/Expense "pay-per-use from your
            Oriora wallet — no subscription"), and the 2026-09-06 copy pass removed every
            "a fraction of a cent" for exactly this reason. Corrected 2026-09-25 to the fleet shape. */}
        <p className="capture-sub">A plate, a package, a label. Oriora reads it and estimates the numbers — you confirm before it saves. No subscription and no trial — pay-per-use from your Oriora wallet, only for the estimates you actually run.</p>
        <div className="capture-modes">
          {platform() !== "web" && (
            <button type="button" className="capture-mode" onClick={() => startForm(undefined, undefined, todayLocal(), "label")}>Scan label — on your device</button>
          )}
          <button type="button" className="capture-mode" onClick={() => startForm(undefined, undefined, todayLocal(), "barcode")}>Barcode — free lookup</button>
          <button type="button" className="capture-mode" onClick={() => startForm(undefined, undefined, todayLocal())}>Type it</button>
        </div>
        <p className="capture-note">The photo passes through Oriora for this one estimate, which doesn’t keep it. The saved meal stays on this device unless you back it up.</p>
      </section>

      <TodayBoard
        date={todayLocal()}
        slots={splitSlots(todayMeals)}
        calories={todayCalories}
        signals={homeSignals}
        onAdd={(slot) => startForm(undefined, slot || undefined, todayLocal())}
        onEdit={(m) => startForm(m)}
        onDelete={(m) => setConfirmDelete(m)}
      />

      <WeightCard weights={weights} onOpenHistory={() => setShowWeights(true)} onSaved={reload} />

      <div ref={aiRef}>
        <RecapAndAsk hasData={meals.length > 0 || weights.length > 0} offline={offline} />
      </div>

      <div className="sec-head">
        <h2><Utensils size={17} style={{ color: "var(--accent)" }} /> Earlier days {earlierCount > 0 && <span className="muted tnum" style={{ fontWeight: 400 }}>({earlierCount} meals)</span>}</h2>
      </div>

      {meals.length === 0 ? (
        <div className="card fade-up empty-glow" style={{ padding: "2.75rem 1.75rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
          <div className="empty-art"><img src={emptyFirstCapture} alt="" aria-hidden="true" /></div>
          <div className="serif" style={{ fontSize: "1.25rem", margin: "0.85rem 0 0.4rem" }}>Log your first meal</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, maxWidth: 330, margin: "0 auto" }}>
            Type it or snap a photo — figures are estimates. Your log stays on your device; a photo or AI estimate passes through Oriora, which doesn't keep it.
          </div>
          <button className="btn btn-primary btn-pill" style={{ marginTop: "1.25rem" }} onClick={() => startForm()}>
            <Plus size={16} /> Log a meal
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.85rem" }}>
          {byDay.filter((g) => g.date !== todayLocal()).map((g) => (
            <div key={g.date}>
              <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.35rem" }}>
                <span className="muted" style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>{prettyDate(g.date)}</span>
                <span className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>~{g.calories.toLocaleString()} kcal est.</span>
              </div>
              <div className="day-slots">
                {splitSlots(g.rows).map((sl) => (
                  <div key={sl.slot || "other"} className={`slot-row${sl.rows.length === 0 ? " slot-open" : ""}`}>
                    <div className="slot-rail">
                      <span className="slot-name">{sl.label}</span>
                      {sl.rows.length > 0 && <span className="slot-kcal tnum">~{sl.calories.toLocaleString()}</span>}
                    </div>
                    <div className="slot-body">
                      {sl.rows.length === 0 ? (
                        <button className="slot-add" onClick={() => startForm(undefined, sl.slot || undefined, g.date)}>
                          <Plus size={13} /> nothing logged
                        </button>
                      ) : (
                        sl.rows.map((m) => (
                          <MealTile key={m.id} m={m} onEdit={() => startForm(m)} onDelete={() => setConfirmDelete(m)} />
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <MealForm editing={editing} presetSlot={preset.slot} presetDate={preset.date} initialAssist={preset.assist} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); void reload(); }} />
      )}

      {showWeights && (
        <WeightHistoryModal weights={weights} onClose={() => setShowWeights(false)} onChanged={() => void reload()} />
      )}

      {confirmDelete && (
        <ModalShell title="Delete meal?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.55 }}>
            Delete "<strong style={{ color: "var(--ink-1)" }}>{confirmDelete.name}</strong>" ({prettyDate(confirmDelete.eaten_on)})? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-danger btn-pill" onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive. */}
      {!chatOpen && (
        <button className="rbc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <PlateMark size={19} />
        </button>
      )}
      <aside className={`rbc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="rbc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to log, find, or ask</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

// ── THE DAY IN YOUR OWN RANGE — this app's own bones (4.3 differentiation, 2026-07-27) ──
//
// Every calorie tracker in the category (MyFitnessPal, Lose It, Cronometer, MacroFactor) leads
// with the same thing: a BUDGET RING — today's number against a goal, and how much you have
// "left". Meal Tracker cannot do that and must not: its health fence forbids goals, targets,
// calorie budgets and diet prescriptions outright. Copying the ring would mean breaking the
// fence; leaving a bare "~1,800 kcal" number says nothing, because a number means nothing
// without something to read it against.
//
// So read it against the person's OWN record instead of a target. signals.rs already computes
// the whole rhythm on-device for free — 14-day series, this week vs last, the weekday that runs
// highest — and none of it was on screen; it existed only to feed the paid recap, while the home
// showed the generic big-number hero shared with every other Oriora app.
//
// That is the differentiation AND the honest reading of the fence: we describe the log, we never
// prescribe against it. No target is named, nothing is scored good or bad, and the range shown is
// the user's own — which is why the band deliberately has no colour coding.
/** TODAY, AS THE SHAPE OF A DAY.
 *
 *  This is the app's own primitive (ledger row 2b). The scaffold every sibling app shares is
 *  header → tinted band → full-width pill → vertical stack of white cards → chat FAB; changing the
 *  band's WORDS is paint, so this replaces the band itself. A food log's object is a meal that
 *  belongs to a part of a day, so the day — Breakfast/Lunch/Dinner/Snacks, filled or open — IS the
 *  layout, the way every food-logging app does it, because "what does today look like" is the
 *  question people open the app with. A card list can only answer "what exists".
 *
 *  🔒 Health fence: an open part of the day is DESCRIPTIVE, never a target or a prompt to eat. It
 *  says "nothing logged" and nothing more. No goal line, no ring, no colour-coded judgement — this
 *  app is observational by contract, which is exactly why it cannot use the category's usual
 *  calorie-budget ring, and why the day's SHAPE is the honest thing to show instead. */
/** Slot → its plate glyph (delivery assets, explicit hex — safe inside <img>). */
const SLOT_GLYPH: Record<string, string> = {
  breakfast: glyphBreakfast, lunch: glyphLunch, dinner: glyphDinner, snack: glyphSnacks,
};

function TodayBoard({ date, slots, calories, signals, onAdd, onEdit, onDelete }: {
  date: string;
  slots: { slot: MealSlot; label: string; rows: Meal[]; calories: number }[];
  calories: number;
  signals: HealthSignals | null;
  onAdd: (slot: MealSlot) => void;
  onEdit: (m: Meal) => void;
  onDelete: (m: Meal) => void;
}) {
  const logged = slots.filter((s) => s.rows.length > 0).length;
  // The range is the spread of the person's OWN logged days — derived here, never a target.
  // Today is excluded so a part-logged day can't drag its own floor down.
  const past = (signals?.daily_totals_14d ?? []).filter((d) => d.date !== date && d.calories > 0);
  const lo = past.length >= 3 ? Math.min(...past.map((d) => d.calories)) : null;
  const hi = past.length >= 3 ? Math.max(...past.map((d) => d.calories)) : null;
  const inRange = lo != null && hi != null && hi > lo;
  const pos = inRange ? Math.min(100, Math.max(0, ((calories - lo) / (hi - lo)) * 100)) : null;

  return (
    <section className="today-board fade-up" aria-label={`Today, ${prettyDate(date)}`}>
      <div className="tb-head">
        <span className="tb-day">Your plates</span>
        <span className="tb-total tnum">~{calories.toLocaleString()} kcal est.</span>
      </div>

      {/* Four plates — one per part of the day, tinted when filled, clean when open. */}
      <div className="plate-row">
        {slots.filter((sl) => sl.slot).map((sl) => (
          <button
            key={sl.slot}
            type="button"
            className={`plate${sl.rows.length ? " filled" : ""}`}
            data-slot={sl.slot === "snack" ? "snacks" : sl.slot}
            onClick={() => onAdd(sl.slot)}
            aria-label={sl.rows.length ? `${sl.label}: ~${sl.calories.toLocaleString()} kcal logged` : `${sl.label}: nothing logged`}
          >
            <span className="plate-disc">
              <img src={SLOT_GLYPH[sl.slot] ?? glyphSnacks} alt="" aria-hidden="true" width={32} height={32} />
            </span>
            <span className="plate-label">{sl.label}</span>
            {sl.rows.length > 0 && <span className="plate-kcal tnum">~{sl.calories.toLocaleString()}</span>}
          </button>
        ))}
      </div>

      <div className="tb-slots" style={{ marginTop: 18 }}>
        {slots.map((sl) => sl.rows.length ? (
          // A filled slot lists its meals as tiles with Edit/Delete — the same MealTile the earlier
          // days use — so a meal logged today can be corrected today. Adding more stays on the plates.
          <div key={sl.slot || "other"} className="tb-slot filled" style={{ cursor: "default", alignItems: "start" }}>
            <span className="tb-slot-name" style={{ paddingTop: 12 }}>{sl.label}</span>
            <div className="slot-body" style={{ gridColumn: "2 / -1" }}>
              {sl.rows.map((m) => (
                <MealTile key={m.id} m={m} onEdit={() => onEdit(m)} onDelete={() => onDelete(m)} />
              ))}
            </div>
          </div>
        ) : (
          <button
            key={sl.slot || "other"}
            className="tb-slot"
            onClick={() => onAdd(sl.slot)}
            aria-label={`${sl.label}: nothing logged`}
          >
            <span className="tb-slot-name">{sl.label}</span>
            <span className="tb-slot-empty">nothing logged</span>
          </button>
        ))}
      </div>

      {/* The range line is a DESCRIPTION of the person's own logged spread — never a target. */}
      <div className="tb-foot">
        {pos != null ? (
          <>
            <div className="tb-range" role="img" aria-label={`Today sits between your logged ${lo} and ${hi} kcal days`}>
              <span className="tb-range-dot" style={{ left: `${pos}%` }} />
            </div>
            <span className="muted">
              so far today, against your logged days ({lo!.toLocaleString()}–{hi!.toLocaleString()})
            </span>
          </>
        ) : (
          <span className="muted">
            {logged === 0 ? "Nothing logged yet today" : `${logged} of the day logged`} · estimates, not advice
          </span>
        )}
      </div>
    </section>
  );
}

function TodayHero({ meals, calories, totalMeals, daysLogged, signals }: {
  meals: Meal[]; calories: number; totalMeals: number; daysLogged: number; signals: HealthSignals | null;
}) {
  const protein = meals.reduce((s, m) => s + (m.protein_g ?? 0), 0);
  const carbs = meals.reduce((s, m) => s + (m.carbs_g ?? 0), 0);
  const fat = meals.reduce((s, m) => s + (m.fat_g ?? 0), 0);
  // Only meals that actually carry macro data count — otherwise the card read "0p · 0c · 0f"
  // after logging meals without macros (review finding: "estimate grams 000 after 2 meals?!").
  const hasMacros = meals.some((m) => m.protein_g != null || m.carbs_g != null || m.fat_g != null);

  // The user's own span, from days that actually have data. Needs 3+ days before a "range"
  // means anything — below that we say so rather than draw a band off two points.
  const days = (signals?.daily_totals_14d ?? []).filter((d) => d.calories > 0);
  const cals = days.map((d) => d.calories);
  const lo = cals.length ? Math.min(...cals) : 0;
  const hi = cals.length ? Math.max(...cals) : 0;
  const hasRange = days.length >= 3 && hi > lo;
  // Where today sits inside that span, 0–1. Clamped: today can legitimately fall outside the
  // previous range, and the marker should sit at the edge rather than off the bar.
  const pos = hasRange ? Math.min(1, Math.max(0, (calories - lo) / (hi - lo))) : 0.5;
  const typical = days.length ? Math.round(cals.reduce((a, b) => a + b, 0) / cals.length) : 0;

  return (
    <div className="fade-up" style={{ marginBottom: "1.1rem" }}>
      <div className="hero" style={{ marginBottom: totalMeals === 0 ? 0 : "0.75rem" }}>
        <div className="eyebrow" style={{ color: "var(--accent-soft-ink)", opacity: 0.85, marginBottom: "0.35rem" }}>
          <Flame size={13} /> Today
        </div>
        <div className="serif tnum" style={{ fontSize: "2.1rem", fontWeight: 600, lineHeight: 1.05 }}>
          {meals.length === 0 ? "Nothing logged yet" : `~${calories.toLocaleString()} kcal`}
        </div>

        {meals.length === 0 ? (
          <div style={{ fontSize: "var(--fs-sm)", opacity: 0.85, marginTop: 2 }}>
            Log a meal to get started — all figures are estimates
          </div>
        ) : hasRange ? (
          <>
            {/* The band IS the range of the user's own logged days. The marker is today. No
                target line, because there is no target — that is the fence, drawn. */}
            <div style={{ marginTop: "0.7rem", position: "relative", height: 8, borderRadius: 999,
                          background: "linear-gradient(90deg, var(--border-subtle), var(--accent-soft))" }}>
              <div aria-hidden style={{ position: "absolute", top: -3, bottom: -3, width: 3, borderRadius: 2,
                                        left: `calc(${(pos * 100).toFixed(1)}% - 1.5px)`, background: "var(--ink-1)" }} />
            </div>
            <div className="row" style={{ justifyContent: "space-between", marginTop: 4, fontSize: "var(--fs-xxs)", opacity: 0.75 }}>
              <span className="tnum">{lo.toLocaleString()}</span>
              <span>your range over {days.length} logged days</span>
              <span className="tnum">{hi.toLocaleString()}</span>
            </div>
            <div style={{ fontSize: "var(--fs-sm)", opacity: 0.85, marginTop: "0.5rem" }}>
              {meals.length} meal{meals.length === 1 ? "" : "s"} today · your typical logged day is
              {" "}<span className="tnum">~{typical.toLocaleString()}</span> kcal (estimates)
            </div>
          </>
        ) : (
          <div style={{ fontSize: "var(--fs-sm)", opacity: 0.85, marginTop: 2 }}>
            {meals.length} meal{meals.length === 1 ? "" : "s"} logged today (estimates) · log a few
            more days and this shows where today sits in your own range
          </div>
        )}
      </div>

      {totalMeals > 0 && (
        <div className="stat-grid">
          <div className="stat">
            <div className="stat-lbl"><Utensils size={12} /> Today's macros (est.)</div>
            <div className="stat-val tnum" style={{ fontSize: "1rem" }}>
              {hasMacros ? `${Math.round(protein)}p · ${Math.round(carbs)}c · ${Math.round(fat)}f` : "no macro data yet"}
            </div>
            <div className="stat-sub">grams, from what you logged</div>
          </div>
          {/* The weekday pattern is the thing a budget ring structurally cannot show, and it comes
              free from signals.rs. Falls back to the all-time count until there are 2+ samples. */}
          {signals?.highest_avg_weekday ? (
            <div className="stat">
              <div className="stat-lbl"><CalendarDays size={12} /> Runs highest on</div>
              <div className="stat-val" style={{ fontSize: "1rem" }}>{signals.highest_avg_weekday}</div>
              <div className="stat-sub">
                {signals.lowest_avg_weekday ? `lowest on ${signals.lowest_avg_weekday}` : "across your logged weeks"}
              </div>
            </div>
          ) : (
            <div className="stat">
              <div className="stat-lbl"><Flame size={12} /> All time</div>
              <div className="stat-val tnum">{totalMeals}</div>
              <div className="stat-sub">meals across {daysLogged} day{daysLogged === 1 ? "" : "s"}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Weight: quick log + latest/trend chips ────────────────────────────────────────
function WeightCard({ weights, onOpenHistory, onSaved }: { weights: WeightEntry[]; onOpenHistory: () => void; onSaved: () => void }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const latest = weights[0] ?? null;
  // 30-day delta shown as a plain fact.
  const delta = useMemo(() => {
    if (weights.length < 2) return null;
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    const cut = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, "0")}-${String(cutoff.getDate()).padStart(2, "0")}`;
    const window = weights.filter((w) => w.measured_on >= cut);
    if (window.length < 2) return null;
    const d = window[0].weight_kg - window[window.length - 1].weight_kg;
    return Math.abs(d) < 0.05 ? null : Math.round(d * 10) / 10;
  }, [weights]);

  async function log() {
    const v = parseNum(val);
    if (v === null || v <= 0 || v > 700) { setErr("Enter a weight in kg (e.g. 72.5)."); return; }
    setBusy(true); setErr(null);
    try {
      await store.upsertWeight({ weight_kg: v, measured_on: todayLocal() });
      setVal("");
      onSaved();
    } catch { setErr("Could not save — try again."); }
    finally { setBusy(false); }
  }

  return (
    <div className="card fade-up" style={{ padding: "0.9rem 1.1rem", marginTop: "0.85rem" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
        <div className="eyebrow"><Scale size={14} /> Weight</div>
        {latest && (
          <span className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>
            latest {latest.weight_kg} kg · {relativeDays(latest.measured_on)}
            {delta !== null && ` · ${delta > 0 ? "+" : ""}${delta} kg in 30d`}
          </span>
        )}
      </div>
      <form className="row" style={{ gap: "0.4rem", marginTop: "0.6rem", flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); void log(); }}>
        <input inputMode="decimal" placeholder="Today's weight, kg — e.g. 72.5" value={val} onChange={(e) => setVal(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <button type="submit" className="btn btn-pill" style={{ flexShrink: 0 }} disabled={busy || !val.trim()}>{busy ? "Saving…" : "Log"}</button>
        {weights.length > 0 && (
          <button type="button" className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onOpenHistory}>History</button>
        )}
      </form>
      {err && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.4rem", color: "var(--warning)" }}>{err}</div>}
    </div>
  );
}

function WeightHistoryModal({ weights, onClose, onChanged }: { weights: WeightEntry[]; onClose: () => void; onChanged: () => void }) {
  async function del(id: string) {
    await store.deleteWeight(id);
    onChanged();
  }
  return (
    <ModalShell title="Weight history" onCancel={onClose}>
      {weights.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", textAlign: "center", padding: "1rem 0" }}>No entries yet.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem", maxHeight: 380, overflowY: "auto" }}>
          {weights.map((w) => (
            <div key={w.id} className="row" style={{ justifyContent: "space-between", gap: "0.6rem", padding: "0.6rem 0.7rem", background: "var(--elev-2)", borderRadius: 12 }}>
              <div className="row" style={{ gap: "0.5rem", minWidth: 0 }}>
                <span className="tnum" style={{ fontWeight: 600, fontSize: "var(--fs-xs)" }}>{w.measured_on}</span>
                <span className="tnum" style={{ fontSize: "var(--fs-sm)" }}>{w.weight_kg} kg</span>
              </div>
              <button className="icon-btn danger" title="Remove" aria-label="Remove entry" style={{ flexShrink: 0 }} onClick={() => del(w.id)}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "1.25rem" }}>
        <button className="btn btn-pill" onClick={onClose}>Close</button>
      </div>
    </ModalShell>
  );
}

// ── The billed AI features: weekly recap + ask (ONE call per tap, computed signals only) ─
type AskErr = { code: BrainErrorCode | "empty"; message: string };

function RecapAndAsk({ hasData, offline }: { hasData: boolean; offline: boolean }) {
  const [question, setQuestion] = useState("");
  const [output, setOutput] = useState<string | null>(null);
  const [signals, setSignals] = useState<HealthSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<AskErr | null>(null);
  // Live, instant client-side hint so the user sees the fence BEFORE pressing the button.
  const medicalHint = useMemo(() => medicalRefusal(question), [question]);

  async function run(mode: "recap" | "ask") {
    setBusy(true); setErr(null); setOutput(null); setSignals(null); setFresh(false);
    try {
      const sig = await store.computeSignals(todayLocal());
      const text = mode === "recap"
        ? (await weeklyRecap(sig)).recap
        : (await askHealth(sig, question)).answer;
      if (!text.trim()) {
        setErr({ code: "empty", message: "Nothing came back — try again once you've logged a few meals." });
      } else {
        setOutput(text.trim());
        setSignals(sig);
        setFresh(true);
        setTimeout(() => setFresh(false), 700);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "The request failed — please try again." });
    } finally { setBusy(false); }
  }

  if (!hasData) return null;
  return (
    <div className={`recap fade-up${fresh ? " fresh" : ""}`} style={{ padding: "1.1rem 1.25rem", margin: "0.85rem 0 0.5rem" }}>
      <div className="eyebrow" style={{ flexWrap: "wrap" }}>
        <Sparkles size={14} style={{ flexShrink: 0 }} /> Your week, described
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8, lineHeight: 1.55 }}>
        Reads back what <strong style={{ color: "var(--ink-2)" }}>you</strong> logged — totals, week-over-week changes, streaks, weight movement. Descriptive only: estimates, not advice, no targets.
      </div>

      <input
        placeholder="Or ask about your log (optional) — e.g. which day had the most meals?"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        style={{ marginTop: "0.8rem" }}
        onKeyDown={(e) => { if (e.key === "Enter" && !busy && !medicalHint && !offline && question.trim()) void run("ask"); }}
      />

      {/* Medical fence — instant, client-side, BEFORE any billed call. */}
      {medicalHint && (
        <div className="notice notice-warn" style={{ marginTop: 10 }}>
          <AlertCircle size={14} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
          <span>{medicalHint}</span>
        </div>
      )}

      <div className="row" style={{ justifyContent: "space-between", marginTop: "0.8rem", gap: "0.6rem", flexWrap: "wrap" }}>
        <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 160 }}>One tap = one Oriora call. Your log stays on your device; only what you send for a recap or estimate passes through Oriora (not retained).</span>
        <div className="row" style={{ gap: "0.4rem", flexShrink: 0 }}>
          {question.trim() && !medicalHint && (
            <button className="btn btn-sm btn-pill" disabled={busy || offline} onClick={() => void run("ask")}>
              {busy ? "…" : "Ask"}
            </button>
          )}
          <button className="btn btn-primary btn-sm btn-pill" disabled={busy || offline} onClick={() => void run("recap")}>
            {busy ? (<><Sparkles size={12} className="pulse-soft" /> Reading…</>) : output ? (<><RefreshCw size={12} /> Recap again</>) : (<><Sparkles size={13} /> Recap my week</>)}
          </button>
        </div>
      </div>

      {/* Loading state — a calm shimmer that reserves the result column (no layout shift). */}
      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Flame size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Reading your log…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "70%" }} />
        </div>
      )}

      {/* Error states — each typed, with the right affordance. */}
      {err && !busy && <AskError err={err} onRetry={() => void run(question.trim() && !medicalHint ? "ask" : "recap")} />}

      {/* Success — the recap + the headline facts it drew from + a copy button. */}
      {output && !busy && (
        <div className="fade-up" style={{ marginTop: "0.85rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{output}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <FactList signals={signals} />}
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Describes your own logged estimates — not medical, diet, or health advice.</span>
            <CopyButton text={copyableNarration(output, signals)} />
          </div>
        </div>
      )}
    </div>
  );
}

function AskError({ err, onRetry }: { err: AskErr; onRetry: () => void }) {
  // Medical fence (also enforced pre-call, but surfaced here defensively).
  if (err.code === "refused_medical") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <AlertCircle size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ color: "var(--ink-2)" }}>{err.message}</div>
      </div>
    );
  }
  // Insufficient funds — point to the Oriora account on the web, no amounts shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to keep asking</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Session expired — needs re-auth.
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Empty result — gentle, no retry button.
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  // Network / unavailable — recoverable, offer a retry.
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
        <RefreshCw size={12} /> Retry
      </button>
    </div>
  );
}

// Ghost copy-button for AI results (DESIGN-SYSTEM §7.5 — every Oriora result is copyable).
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

// Build the plain-text copy payload: the narration plus its grounding facts (no markup).
function copyableNarration(text: string, signals: HealthSignals | null): string {
  const facts = signals?.headline_facts.slice(0, 5).map((f) => `• ${f.text}`).join("\n") ?? "";
  return facts ? `${text}\n\nThe numbers behind this:\n${facts}` : text;
}

// The receipts: the deterministic, pre-computed facts the recap drew from. Purely
// observational about the user's OWN log — tone tints the dot only, it is not advice.
function FactList({ signals }: { signals: HealthSignals }) {
  const facts = signals.headline_facts.slice(0, 5);
  if (facts.length === 0) return null;
  const dotClass = (t: FactTone) => (t === "good" ? "good" : t === "watch" ? "watch" : "");
  return (
    <div style={{ marginTop: "0.85rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border-subtle)" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>The numbers behind this</div>
      {facts.map((f, i) => (
        <div key={i} className="fact">
          <span className={`fact-dot ${dotClass(f.tone)}`} />
          <span className="fact-text">{f.text}</span>
        </div>
      ))}
    </div>
  );
}

function MealTile({ m, onEdit, onDelete }: { m: Meal; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="tile">
      <button style={{ flex: 1, minWidth: 0, cursor: "pointer", background: "none", border: "none", textAlign: "left", padding: 0 }} onClick={onEdit}>
        <div className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</div>
          {m.source !== "manual" && <span className="badge badge-make" style={{ flexShrink: 0 }}>{SOURCE_LABELS[m.source]}</span>}
        </div>
        <div className="row" style={{ gap: "0.5rem", marginTop: 4 }}>
          <span className="row tnum" style={{ gap: 3, fontSize: 10, color: "var(--ink-3)" }}>
            <Flame size={10} /> ~{m.calories.toLocaleString()} kcal est.
          </span>
          {(m.protein_g != null || m.carbs_g != null || m.fat_g != null) && (
            <span className="tnum" style={{ fontSize: 10, color: "var(--ink-3)" }}>
              {m.protein_g != null ? `${m.protein_g}p` : "–"} · {m.carbs_g != null ? `${m.carbs_g}c` : "–"} · {m.fat_g != null ? `${m.fat_g}f` : "–"}
            </span>
          )}
        </div>
      </button>
      <div className="row" style={{ gap: 2, flexShrink: 0 }}>
        <IconBtn title="Edit meal" onClick={onEdit}><Pencil size={15} /></IconBtn>
        <IconBtn title="Delete meal" danger onClick={onDelete}><Trash2 size={15} /></IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ title, onClick, children, on, danger }: { title: string; onClick: () => void; children: React.ReactNode; on?: boolean; danger?: boolean }) {
  return (
    <button className={`icon-btn${on ? " on" : ""}${danger ? " danger" : ""}`} title={title} aria-label={title}
      onClick={(ev) => { ev.stopPropagation(); onClick(); }}>
      {children}
    </button>
  );
}

// Downscale a picked photo to ≤1024px JPEG data-URL on-device (cloned from the
// plant_diary/closet precedent) — small enough for the device-direct vision call.
async function downscaleToDataUrl(file: File): Promise<string> {
  // Load as a data: URL (NOT a blob: object URL): the app CSP allows `img-src data:` but not
  // `blob:`, so an <img> pointed at a blob URL is BLOCKED → onerror → "unreadable image" →
  // the estimate never runs. FileReader → data: URL sidesteps CSP entirely. (Same fix for any
  // photo→AI app on this scaffold.)
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error("unreadable image"));
    fr.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("unreadable image"));
    i.src = dataUrl;
  });
  const max = 1024;
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.82);
}

// ── Log / edit a meal — with the THREE capture assists ───────────────────────────
//   1. Photo → calories: through the Oriora brain (POST /estimate-photo) — the ROUTER picks an
//      image-capable model; no vendor is named and no key is involved; user confirms via Save.
//   2. Nutrition label: on-device Apple Vision OCR → LOCAL parse (no AI call); if the
//      parse fails, OFFER one billed brain text call on the recognized text.
//   3. Barcode → Open Food Facts, client-side (free public API, no AI, no server).
function MealForm({ editing, presetSlot, presetDate, initialAssist, onClose, onSaved }: { editing: Meal | null; presetSlot?: MealSlot; presetDate?: string; initialAssist?: "photo" | "label" | "barcode"; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [eatenOn, setEatenOn] = useState(editing?.eaten_on ?? presetDate ?? todayLocal());
  const [slot, setSlot] = useState<MealSlot>(editing?.slot ?? presetSlot ?? guessSlot());
  const [calories, setCalories] = useState(editing ? String(editing.calories) : "");
  const [protein, setProtein] = useState(editing?.protein_g != null ? String(editing.protein_g) : "");
  const [carbs, setCarbs] = useState(editing?.carbs_g != null ? String(editing.carbs_g) : "");
  const [fat, setFat] = useState(editing?.fat_g != null ? String(editing.fat_g) : "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [source, setSource] = useState<MealSource>(editing?.source ?? "manual");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Which assist is expanded (add-mode only).
  const isWeb = platform() === "web"; // label OCR + barcode-photo detection are native Apple Vision;
  // the photo ESTIMATE is a server call (§5d) and the barcode TYPED lookup is a client fetch —
  // both work on every surface, so only the genuinely native pieces stay gated.
  const [assist, setAssist] = useState<"photo" | "label" | "barcode" | null>(
    initialAssist === "label" && isWeb ? null : initialAssist ?? null,
  );

  function applyEstimate(r: MealEstimate) {
    if (r.meal_name) setName(r.meal_name);
    setCalories(String(r.calories_estimate));
    if (r.protein_g != null) setProtein(String(r.protein_g));
    if (r.carbs_g != null) setCarbs(String(r.carbs_g));
    if (r.fat_g != null) setFat(String(r.fat_g));
    setSource("photo");
  }

  async function save() {
    if (!name.trim()) { setErr("Give the meal a name."); return; }
    setSaving(true); setErr(null);
    try {
      let kcal = parseNum(calories);
      let p = parseNum(protein), c = parseNum(carbs), f = parseNum(fat);
      // No calories typed → the AI estimates from the name. This is the point of the app:
      // the customer says "2 boiled eggs and toast", we carry the number burden. Never block.
      if (kcal === null) {
        try {
          const est = await estimateMeal(name.trim());
          if (est.calories != null) {
            kcal = est.calories;
            p = p ?? est.protein_g; c = c ?? est.carbs_g; f = f ?? est.fat_g;
          }
        } catch { /* estimator unavailable → save with no number, editable any time */ }
      }
      const payload = {
        name: name.trim(),
        eaten_on: eatenOn,
        calories: Math.round(kcal ?? 0),
        protein_g: p,
        carbs_g: c,
        fat_g: f,
        source,
        slot,
        note: note.trim(),
      };
      if (isEditing) await store.updateMeal(editing!.id, payload);
      else await store.createMeal(payload);
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg === "name_required" ? "Give the meal a name." : `Could not save — try again. (${msg})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit meal" : "Log a meal"} onCancel={onClose} wide>
      {/* Capture assists — add-mode, native surfaces only (photo/label need the device). */}
      {!isEditing && (
        <div style={{ marginBottom: "1rem" }}>
          <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
            <button type="button" className={`chip toggle${assist === "photo" ? " selected" : ""}`} onClick={() => setAssist(assist === "photo" ? null : "photo")}>
              <Camera size={13} /> Snap a photo
            </button>
            {!isWeb && (
              <button type="button" className={`chip toggle${assist === "label" ? " selected" : ""}`} onClick={() => setAssist(assist === "label" ? null : "label")}>
                <ScanLine size={13} /> Scan label
              </button>
            )}
            <button type="button" className={`chip toggle${assist === "barcode" ? " selected" : ""}`} onClick={() => setAssist(assist === "barcode" ? null : "barcode")}>
              <Barcode size={13} /> Barcode
            </button>
          </div>
          {assist === "photo" && <PhotoAssist onEstimate={applyEstimate} />}
          {assist === "label" && !isWeb && (
            <LabelAssist onParsed={(p) => {
              if (p.name) setName(p.name);
              if (p.calories != null) setCalories(String(p.calories));
              if (p.protein_g != null) setProtein(String(p.protein_g));
              if (p.carbs_g != null) setCarbs(String(p.carbs_g));
              if (p.fat_g != null) setFat(String(p.fat_g));
              setSource("label");
            }} />
          )}
          {assist === "barcode" && (
            <BarcodeAssist onFound={(f) => {
              if (f.name) setName(f.name);
              if (f.calories != null) setCalories(String(f.calories));
              if (f.protein_g != null) setProtein(String(f.protein_g));
              if (f.carbs_g != null) setCarbs(String(f.carbs_g));
              if (f.fat_g != null) setFat(String(f.fat_g));
              if (f.basisNote) setNote((n) => (n.trim() ? n : f.basisNote ?? ""));
              setSource("barcode");
            }} />
          )}
        </div>
      )}

      <label style={fieldLabel}>Meal</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chicken salad" autoFocus={!isEditing && assist === null} style={{ marginBottom: "1rem" }} />

      <label style={fieldLabel}>Part of the day</label>
      <div className="slot-pick" style={{ marginBottom: "1rem" }}>
        {MEAL_SLOTS.map((sl) => (
          <button
            key={sl}
            type="button"
            className={`slot-chip${slot === sl ? " on" : ""}`}
            aria-pressed={slot === sl}
            onClick={() => setSlot(slot === sl ? "" : sl)}
          >
            {SLOT_LABEL[sl]}
          </button>
        ))}
      </div>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start", flexWrap: "nowrap", marginBottom: "1rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={fieldLabel}>When</label>
          <input type="date" value={eatenOn} max={todayLocal()} onChange={(e) => setEatenOn(e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={fieldLabel}>Calories <span className="muted" style={{ fontWeight: 400 }}>(est., kcal)</span></label>
          <input inputMode="numeric" placeholder="e.g. 450" value={calories} onChange={(e) => setCalories(e.target.value)} />
        </div>
      </div>

      <label style={fieldLabel}>Macros <span className="muted" style={{ fontWeight: 400 }}>(optional, grams — estimates)</span></label>
      <div className="row" style={{ gap: "0.6rem", flexWrap: "nowrap", marginBottom: "1rem" }}>
        <input inputMode="decimal" placeholder="Protein" value={protein} onChange={(e) => setProtein(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <input inputMode="decimal" placeholder="Carbs" value={carbs} onChange={(e) => setCarbs(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <input inputMode="decimal" placeholder="Fat" value={fat} onChange={(e) => setFat(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
      </div>

      <label style={fieldLabel}>Note <span className="muted" style={{ fontWeight: 400 }}>(optional — stored on this device unless you back it up)</span></label>
      <textarea rows={2} placeholder="Half portion, restaurant guess…" value={note} onChange={(e) => setNote(e.target.value)} style={{ marginBottom: "1.25rem", resize: "vertical" }} />

      {err && <div className="notice notice-err" style={{ marginBottom: "1rem" }}><AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{err}</span></div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn btn-pill" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-pill" onClick={save} disabled={!name.trim() || saving}>{saving ? (<><Sparkles size={13} className="pulse-soft" /> Saving…</>) : isEditing ? "Update" : "Save meal"}</button>
      </div>
    </ModalShell>
  );
}

// ── Assist 1: Photo → calories (through the Oriora brain; the router picks the model) ─
function PhotoAssist({ onEstimate }: { onEstimate: (r: MealEstimate) => void }) {
  // Photo → the ORIORA BRAIN (POST /estimate-photo). The router picks an image-capable model.
  // NO key, NO setup — exactly like every other AI call in the app (same as Expense's receipts).
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function onPick(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setBusy(true); setMsg("Estimating…"); setConfidence(null);
    try {
      const dataUrl = await downscaleToDataUrl(file);
      const r = await estimateMealPhoto(dataUrl);
      onEstimate(r);
      setConfidence(r.confidence);
      setMsg(`Estimated: ${r.meal_name || "your meal"} — review the numbers, then save.`);
    } catch (e) {
      const s = e instanceof Error ? e.message : String(e);
      setMsg(s || "Couldn't read that photo — try a clearer shot.");
    } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: "0.8rem 0.9rem", marginTop: "0.6rem", background: "var(--elev-2)" }}>
      <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn-pill row" style={{ gap: "0.35rem" }} onClick={() => inputRef.current?.click()} disabled={busy}>
          <Camera size={14} /> {busy ? "Estimating…" : "Snap / pick a meal photo"}
        </button>
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.4rem" }}>
        Photograph your meal — a plate, the package, or a nutrition label. Oriora reads it and estimates the numbers; you confirm before saving. Rough estimates.
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={onPick} style={{ display: "none" }} />
      {msg && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.5rem" }}>
          {msg}{confidence && <span className="badge badge-make" style={{ marginLeft: 6 }}>{confidence} confidence</span>}
        </div>
      )}
    </div>
  );
}

// ── Assist 2: nutrition-label scan (on-device OCR → LOCAL parse; AI text call only as fallback) ─
function LabelAssist({ onParsed }: { onParsed: (p: { name: string; calories: number | null; protein_g: number | null; carbs_g: number | null; fat_g: number | null }) => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [ocrText, setOcrText] = useState<string | null>(null); // held for the AI fallback offer
  const inputRef = useRef<HTMLInputElement>(null);

  async function onPick(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setBusy(true); setMsg("Reading the label on-device…"); setOcrText(null);
    try {
      const ext = (file.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const bytes = Array.from(new Uint8Array(await file.arrayBuffer()));
      const path = await store.saveLabelPhoto(bytes, ext);
      let text = "";
      try { text = (await store.ocrImage(path)).trim(); }
      catch { setMsg("On-device scan isn't available here — type the numbers below."); return; }
      if (!text) { setMsg("Couldn't read any text — try a clearer, straight-on photo of the label."); return; }
      const p = parseNutritionLabel(text);
      if (labelParseUsable(p)) {
        onParsed({ name: "", calories: p.calories, protein_g: p.protein_g, carbs_g: p.carbs_g, fat_g: p.fat_g });
        setMsg(`Read the label (per ${p.basis === "100g" ? "100 g" : "serving"}) — review the numbers, then save. No AI call needed.`);
      } else {
        setOcrText(text);
        setMsg("Couldn't parse the numbers from that label.");
      }
    } catch { setMsg("Couldn't read that photo — try again."); }
    finally { setBusy(false); }
  }

  // The billed FALLBACK: one brain text call over the on-device OCR text (never the photo).
  async function aiFallback() {
    if (!ocrText) return;
    setBusy(true); setMsg("Asking the AI to read the label text…");
    try {
      const r = await parseLabelWithAI(ocrText);
      onParsed({ name: r.name, calories: r.calories, protein_g: r.protein_g, carbs_g: r.carbs_g, fat_g: r.fat_g });
      setOcrText(null);
      setMsg("Read — review the numbers, then save.");
    } catch (e) {
      setMsg(e instanceof BrainError ? e.message : "That didn't work — type the numbers below.");
    } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: "0.8rem 0.9rem", marginTop: "0.6rem", background: "var(--elev-2)" }}>
      <button type="button" className="btn btn-pill row" style={{ gap: "0.35rem" }} onClick={() => inputRef.current?.click()} disabled={busy}>
        <ScanLine size={14} /> {busy ? "Reading…" : "Photograph the nutrition label"}
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={onPick} style={{ display: "none" }} />
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.4rem" }}>
        {/* §7b-verified: accurate. The label scan is on-device Apple Vision OCR, the billed
            fallback sends parseLabelWithAI(ocrText) — TEXT, never the photo — and the photo is
            never persisted, so Cloud Backup cannot relay it either. */}
        Read on your device (no AI call for a printed label) — the photo never leaves this device. {/* §7b-verified — on-device Vision OCR; only text is sent; photo never persisted */}
      </div>
      {msg && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.5rem" }}>{msg}</div>}
      {ocrText && !busy && (
        <button type="button" className="btn btn-sm btn-pill" style={{ marginTop: "0.5rem" }} onClick={() => void aiFallback()}>
          <Sparkles size={12} /> Ask the AI to read it (one Oriora call — text only)
        </button>
      )}
    </div>
  );
}

// ── Assist 3: barcode → Open Food Facts (free public API, CORS, no key, no AI, no server) ─
function BarcodeAssist({ onFound }: { onFound: (f: { name: string; calories: number | null; protein_g: number | null; carbs_g: number | null; fat_g: number | null; basisNote?: string }) => void }) {
  const [barcode, setBarcode] = useState("");
  const [looking, setLooking] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  // Photo-first: snap the barcode → on-device Vision detects the digits → straight to lookup.
  // The photo never leaves the device. Manual digits stay below as the fallback.
  async function onBarcodePhoto(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setScanning(true); setMsg("Reading the barcode…");
    try {
      const bytes = Array.from(new Uint8Array(await file.arrayBuffer()));
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = await store.saveLabelPhoto(bytes, ext);
      const code = await store.detectBarcode(path);
      setBarcode(code);
      setMsg(null);
      await lookup(code);
    } catch (er) {
      const s = typeof er === "string" ? er : er instanceof Error ? er.message : String(er);
      setMsg(s || "Couldn't read a barcode in that photo — try a closer shot, or type the digits below.");
    } finally { setScanning(false); }
  }

  async function lookup(fromPhoto?: string) {
    const code = (fromPhoto ?? barcode).replace(/[^0-9]/g, "");
    if (code.length < 8) { setMsg("Enter the product barcode (8–13 digits)."); return; }
    setLooking(true); setMsg(null);
    try {
      const r = await fetch(`https://world.openfoodfacts.org/api/v2/product/${code}.json?fields=product_name,brands,nutriments,serving_size`);
      const j = (await r.json()) as {
        status?: number;
        product?: {
          product_name?: string; brands?: string; serving_size?: string;
          nutriments?: Record<string, number | string>;
        };
      };
      if (j.status !== 1 || !j.product) { setMsg("No match found — enter the details manually."); return; }
      const p = j.product;
      const n = p.nutriments ?? {};
      const num = (k: string): number | null => {
        const v = Number(n[k]);
        return Number.isFinite(v) && v >= 0 ? Math.round(v * 10) / 10 : null;
      };
      // Prefer per-serving figures when present, else per-100g (noted so the user knows the basis).
      const perServing = num("energy-kcal_serving") != null;
      const suffix = perServing ? "_serving" : "_100g";
      const kcal = num(`energy-kcal${suffix}`);
      const name = [p.brands?.split(",")[0]?.trim(), p.product_name].filter(Boolean).join(" — ");
      onFound({
        name: name || "",
        calories: kcal != null ? Math.round(kcal) : null,
        protein_g: num(`proteins${suffix}`),
        carbs_g: num(`carbohydrates${suffix}`),
        fat_g: num(`fat${suffix}`),
        basisNote: perServing
          ? (p.serving_size ? `Per serving (${p.serving_size}), from Open Food Facts` : "Per serving, from Open Food Facts")
          : "Per 100 g, from Open Food Facts — adjust for your portion",
      });
      setMsg(`Found: ${p.product_name ?? "product"}${perServing ? " (per serving)" : " (per 100 g — adjust for your portion)"}`);
    } catch {
      setMsg("Lookup failed — check your connection or enter it manually.");
    } finally {
      setLooking(false);
    }
  }

  const isWeb = platform() === "web"; // barcode-photo detection is native Apple Vision; typed digits work everywhere
  return (
    <div className="card" style={{ padding: "0.8rem 0.9rem", marginTop: "0.6rem", background: "var(--elev-2)" }}>
      {!isWeb && (
        <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
          <button type="button" className="btn btn-pill row" style={{ gap: "0.35rem" }} onClick={() => photoRef.current?.click()} disabled={scanning || looking}>
            <Camera size={14} /> {scanning ? "Reading…" : "Snap the barcode"}
          </button>
        </div>
      )}
      <input ref={photoRef} type="file" accept="image/*" onChange={onBarcodePhoto} style={{ display: "none" }} />
      <form className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); void lookup(); }}>
        <input inputMode="numeric" placeholder={isWeb ? "Type the barcode digits" : "…or type the digits"} value={barcode}
          onChange={(e) => setBarcode(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <button type="submit" className="btn btn-pill" style={{ flexShrink: 0 }} disabled={looking || scanning || !barcode.trim()}>
          {looking ? "Looking…" : "Look up"}
        </button>
      </form>
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.4rem" }}>
        {isWeb
          ? "Looked up in the public Open Food Facts database — no AI, nothing through Oriora."
          : "Barcode read on this device, then looked up in the public Open Food Facts database — no AI, nothing through Oriora."}
      </div>
      {msg && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.5rem" }}>{msg}</div>}
    </div>
  );
}

const fieldLabel: React.CSSProperties = { display: "block", fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.45rem" };

function ModalShell({ title, subtitle, children, onCancel, wide }: { title: string; subtitle?: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  // Esc closes; restore focus is browser-default. Scrim click closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="sheet-scrim" onClick={onCancel}>
      <div className="sheet sheet-center" onClick={(e) => e.stopPropagation()} style={{ width: wide ? "min(480px, calc(100% - 2rem))" : "min(400px, calc(100% - 2rem))", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.1rem", gap: "0.6rem" }}>
          <div style={{ minWidth: 0 }}>
            <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem" }}>{title}</div>
            {subtitle && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
          </div>
          <button className="icon-btn" aria-label="Close" onClick={onCancel} style={{ flexShrink: 0, marginTop: -2 }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
